import type { ReactNode } from "react";

import { cn } from "../cn";

export interface Choice<T extends string | number> {
  value: T;
  label: ReactNode;
  /** Accessible name when `label` is not text (e.g. a colour swatch). */
  ariaLabel?: string;
}

interface BaseProps<T extends string | number> {
  /** Accessible name of the group. */
  label: string;
  options: readonly Choice<T>[];
  className?: string;
  /** Marks the group invalid (with an error rendered by the caller). */
  invalid?: boolean;
  describedBy?: string;
}

const chip =
  "inline-flex min-h-9 items-center justify-center gap-2 rounded-[var(--radius-pill)] border border-border px-3.5 text-sm transition-colors hover:border-primary/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring aria-checked:border-primary aria-checked:bg-primary aria-checked:text-primary-foreground";

/**
 * Single choice as in-page pill buttons (`role="radiogroup"`). Used instead
 * of a native `<select>`, whose popup is drawn by the OS with system fonts
 * that may lack Bangla.
 */
export function ChoiceChips<T extends string | number>({
  label,
  options,
  value,
  onChange,
  className,
  invalid,
  describedBy,
}: BaseProps<T> & { value: T | undefined; onChange: (value: T) => void }) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-invalid={invalid === true ? true : undefined}
      aria-describedby={describedBy}
      className={cn("flex flex-wrap gap-2", className)}
    >
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          aria-label={o.ariaLabel}
          // Roving tab stop: Tab enters the group once; arrows move within it.
          tabIndex={value === o.value || (value === undefined && o === options[0]) ? 0 : -1}
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => {
            const i = options.findIndex((x) => x.value === o.value);
            const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
            if (step === 0) return;
            e.preventDefault();
            const n = (i + step + options.length) % options.length;
            const next = options[n];
            if (next === undefined) return;
            onChange(next.value);
            const buttons = e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
            buttons?.[n]?.focus();
          }}
          className={chip}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Multiple choice as toggle pills (`aria-pressed`). */
export function MultiChoiceChips<T extends string | number>({
  label,
  options,
  values,
  onChange,
  className,
  invalid,
  describedBy,
}: BaseProps<T> & { values: readonly T[]; onChange: (values: T[]) => void }) {
  return (
    <div
      role="group"
      aria-label={label}
      aria-invalid={invalid === true ? true : undefined}
      aria-describedby={describedBy}
      className={cn("flex flex-wrap gap-2", className)}
    >
      {options.map((o) => {
        const on = values.includes(o.value);
        return (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={on}
            aria-label={o.ariaLabel}
            onClick={() => onChange(on ? values.filter((v) => v !== o.value) : options.filter((x) => x.value === o.value || values.includes(x.value)).map((x) => x.value))}
            className={cn(chip, "aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground")}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
